// Display helpers format a phone string. Dial helpers strip non-digits so
// click-to-call links stay dialable before a DNI provider rewrites them.
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from '../../constants';

/** Last 10 digits when present, otherwise every digit found. */
export const scrubPhoneDigits = (raw?: string | null): string => {
    if (!raw) {
        return '';
    }

    const digits = raw.replace(/\D/g, '');
    if (digits.length >= 10) {
        return digits.slice(-10);
    }

    return digits;
};

/** `tel:+1` plus 10 digits. Vanity national copy maps to the national dial number. */
export const toTelHref = (raw?: string | null): string => {
    if (!raw?.trim()) {
        return '';
    }

    const trimmed = raw.trim();
    if (trimmed === PHONE_NUMBER_DISPLAY) {
        return `tel:${PHONE_NUMBER}`;
    }

    const digits = scrubPhoneDigits(trimmed);
    if (digits.length === 10) {
        return `tel:+1${digits}`;
    }

    if (!digits) {
        return '';
    }

    return `tel:${digits}`;
};

/**
 * Raw dial value wins until a provider replaces the visible number.
 * A replacement equal to the original children is ignored.
 */
export const resolveDialSource = (
    children?: string | null,
    dialNumber?: string | null,
    replaced?: string | null,
): string => {
    if (replaced && children && replaced !== children) {
        return replaced;
    }

    return dialNumber || children || '';
};

/** Digits for stored franchise phones. Vanity national copy stays as display text. */
export const normalizeSourcedPhone = (raw?: string | null): string => {
    const trimmed = raw?.trim() ?? '';
    if (!trimmed || trimmed === PHONE_NUMBER_DISPLAY) {
        return trimmed;
    }

    return scrubPhoneDigits(trimmed);
};

// Takes the last 10 digits of a phone number string and
// returns them as a String in the format: `(xxx) xxx-xxxx`
const formatPhoneLink = (tenDigitFormat?: string|null): string => {
    if (!tenDigitFormat) {
        return '';
    }

    if (tenDigitFormat === PHONE_NUMBER_DISPLAY) {
        return PHONE_NUMBER_DISPLAY;
    }

    const tenDigits = scrubPhoneDigits(tenDigitFormat);
    const areaCode = tenDigits.substr(0, 3);
    const regionNumber = tenDigits.substr(3, 3);
    const subscriberNumber = tenDigits.substr(-4);

    return `(${areaCode}) ${regionNumber}-${subscriberNumber}`;
};

export const formatPhoneNumber = (tenDigitFormat?: string|null): string => {
    if (tenDigitFormat === PHONE_NUMBER_DISPLAY) {
        return PHONE_NUMBER_DISPLAY;
    }

    if (!tenDigitFormat) {
        return '';
    }

    const tenDigits = scrubPhoneDigits(tenDigitFormat);
    const areaCode = tenDigits.substr(0, 3);
    const regionNumber = tenDigits.substr(3, 3);
    const subscriberNumber = tenDigits.substr(-4);

    return `${areaCode}-${regionNumber}-${subscriberNumber}`;
};

export default formatPhoneLink;
